import { gql } from '@apollo/client';
import _ from 'lodash';
import * as monacoEditor from 'monaco-editor';
import { useCallback, useEffect, useMemo } from 'react';

import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { createAutocompleteSuggestion } from '../../explorerPage/helpers/monacoHelpers';

const mentionRegex = /(^|\s)@([^\s',;]+)/g;

export const UseMentionUsersServiceFragment = gql<
  GraphQLTypes.UseMentionUsersServiceFragment,
  {}
>`
  fragment UseMentionUsersServiceFragment on Service {
    id
    account {
      id
      memberships {
        account {
          id
        }
        user {
          id
          fullName
        }
      }
    }
  }
`;

export function useGetUsersForMentions(
  graph: GraphQLTypes.UseMentionUsersServiceFragment | undefined | null,
) {
  const users = useMemo(
    () =>
      graph?.account?.memberships?.map(({ user }) => {
        const safeValue = user.id.replace(/\./g, '_').replace(/@/g, '_');
        return {
          userId: user.id,
          fullName: user.fullName,
          userClass: `u${safeValue}`,
        };
      }) ?? [],
    [graph],
  );
  const userLookup = useMemo(
    () => Object.fromEntries(users.map((user) => [user.userId, user])),
    [users],
  );
  return {
    users,
    transformInputForMarkdown: useCallback(
      (str: string) =>
        str.replace(mentionRegex, (fullMatch, leadingSpace, userId) => {
          const user = userLookup[userId];
          return user ? `${leadingSpace}{{ user.${user.userId} }}` : fullMatch;
        }),
      [userLookup],
    ),
  };
}

let registered = 0;
let disposable: monacoEditor.IDisposable;
export function useMentionUsers({
  users,
}: {
  users: ReturnType<typeof useGetUsersForMentions>['users'];
}) {
  useEffect(() => {
    if (users.length === 0) return;
    registered += 1;
    const unmount = () => {
      registered -= 1;
      if (registered === 0) {
        disposable.dispose();
      }
    };
    if (registered > 1) {
      return unmount;
    } else {
      disposable = monacoEditor.languages.registerCompletionItemProvider(
        'markdown',
        {
          triggerCharacters: ['@'],
          async provideCompletionItems(providerModel, position) {
            const wordStart = providerModel.getWordUntilPosition(position);
            const startPosition1 = providerModel.getPositionAt(
              providerModel.getOffsetAt(
                new monacoEditor.Position(
                  position.lineNumber,
                  wordStart.startColumn,
                ),
              ) - 1,
            );
            const startPosition2 = providerModel.getPositionAt(
              providerModel.getOffsetAt(
                new monacoEditor.Position(
                  position.lineNumber,
                  wordStart.startColumn,
                ),
              ),
            );

            const startCharacter = providerModel.getValueInRange(
              monacoEditor.Range.fromPositions(startPosition1, startPosition2),
            );
            if (startCharacter !== '@') return;
            return createAutocompleteSuggestion(
              providerModel,
              position,
              users.map(({ fullName, userId }) => ({
                label: fullName,
                insertText: `${userId} `,
              })),
            );
          },
        },
      );
      return unmount;
    }
  }, [users]);
}
